import {Chip, Divider, MenuItem, TextField, Typography} from '@mui/material';
import {FC, type RefObject} from 'react';
import {mergeWithDefaults, useGetConfigurations} from '@src/api/configurations';
import ErrorMessage from '@src/components/common/ErrorMessage.tsx';
import LoadingMessage from '@src/components/common/LoadingMessage';
import {LAST_CONFIGURATION_ID_LOCAL_STORAGE_KEY} from '@src/constants/analyze';
import useVisualizationParams from '@src/hooks/useVisualizationParams';

export type ConfigurationSelectionProps = {
  changedRef: RefObject<boolean>;
};

export const ConfigurationSelection: FC<ConfigurationSelectionProps> = ({
  changedRef,
}) => {
  const {
    isLoading,
    data: configurations = [],
    error,
  } = useGetConfigurations({
    select: mergeWithDefaults,
  });

  const {configurationId, setConfigurationId} = useVisualizationParams();
  const readOnlyConfigurations = configurations.filter(
    configuration => configuration.readOnly,
  );
  const userConfigurations = configurations.filter(
    configuration => !configuration.readOnly,
  );

  // So invalid id can't be passed as value to dropdown and trigger console warnings by
  // e.g. configuration with persisted id was deleted since last revisit, or just wrong parameter value
  const hasConfiguration = configurations.some(
    configuration => configuration.id === configurationId,
  );
  const selectedConfigurationId = hasConfiguration ? configurationId : '';

  const onConfigurationChange = (configurationId: string) => {
    setConfigurationId(configurationId);
    window.localStorage.setItem(
      LAST_CONFIGURATION_ID_LOCAL_STORAGE_KEY,
      configurationId,
    );
    changedRef.current = true;
  };

  if (isLoading) {
    return (
      <LoadingMessage rootProps={{sx: {blockSize: 'auto', paddingBlock: 2}}} />
    );
  }

  if (configurations.length === 0) {
    return (
      <ErrorMessage>
        <Typography variant="h6" color="inherit" gutterBottom>
          {error?.message}
        </Typography>
      </ErrorMessage>
    );
  }

  return (
    <TextField
      select
      fullWidth
      label="Configuration"
      value={selectedConfigurationId}
      onChange={e => onConfigurationChange(e.target.value)}
      sx={theme => ({
        backgroundColor: 'background.paper',
        backgroundImage: theme.vars?.overlays[1],
        marginBlockStart: 1.5,
        marginBlockEnd: 1.5,
      })}
      slotProps={{input: {id: 'configuration-select'}}}
    >
      {userConfigurations.map(config => (
        <MenuItem key={config.id} value={config.id}>
          {config.name}
        </MenuItem>
      ))}
      {readOnlyConfigurations.length > 0 && [
        <Divider key="div.1" />,
        readOnlyConfigurations.map(config => (
          <MenuItem key={config.id} value={config.id} sx={{gap: 0.5}}>
            {config.name}{' '}
            <Chip
              label="Read only"
              variant="filled"
              size="small"
              sx={{color: 'secondary.main', fontSize: '.75rem'}}
            />
          </MenuItem>
        )),
      ]}
    </TextField>
  );
};

export default ConfigurationSelection;
